/**
 * offlineQueue — file d'attente hors-ligne du tableau de bord (PWA).
 *
 * Quand le réseau est coupé, les opérations critiques (opérations du carnet
 * de crédit, ventes de la caisse) sont stockées dans IndexedDB puis rejouées
 * automatiquement au retour de la connexion.
 *
 * Sécurité : le `merchant_id` / auteur des écritures n'est JAMAIS lu depuis la
 * file. Il est toujours re-dérivé de la session authentifiée au moment de la
 * synchronisation. Les charges utiles sont validées strictement avant envoi,
 * pour qu'une entrée altérée ne puisse pas injecter d'écriture arbitraire.
 */
import { supabase } from '@/lib/supabase';

const DB_NAME = 'zifek-offline';
const STORE = 'queue';
const DB_VERSION = 1;

export type OfflineOpKind = 'credit_tx' | 'sale' | 'stock_movement' | 'product_update';

/** Vrai lorsque le navigateur signale l'absence de réseau. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Détecte une erreur réseau (et non une erreur métier/validation).
 * Sert à décider s'il faut mettre l'opération en file plutôt que d'échouer.
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /failed to fetch|networkerror|network request|load failed|timeout|aborted|fetch/i.test(msg);
}

export interface CreditTxPayload {
  customerId: number;
  type: 'credit' | 'payment' | 'deposit' | 'adjustment';
  amount: number;
  description: string | null;
  paymentMethod: string | null;
  reversesTransactionId: number | null;
}

export interface SaleItemPayload {
  productId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  currency: string;
}

export interface SalePayload {
  items: SaleItemPayload[];
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: 'cash' | 'card';
  currency: string;
}

/** Mouvement de stock saisi hors-ligne (entrée/sortie d'un produit). */
export interface StockMovementPayload {
  productId: number;
  /** Quantité signée : positive = entrée, négative = sortie. */
  delta: number;
  motif: string | null;
  username: string;
  userId: number;
}

/** Modification d'un produit saisie hors-ligne (ex : correction de stock). */
export interface ProductUpdatePayload {
  productId: number;
  patch: Record<string, unknown>;
}

export type OfflinePayload =
  | CreditTxPayload
  | SalePayload
  | StockMovementPayload
  | ProductUpdatePayload;

export interface OfflineOp {
  id?: number;
  kind: OfflineOpKind;
  payload: OfflinePayload;
  createdAt: number;
}

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueOp(kind: OfflineOpKind, payload: OfflinePayload): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).add({ kind, payload, createdAt: Date.now() } as OfflineOp);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  window.dispatchEvent(new CustomEvent('zifek-queue-changed'));
}

export async function getQueuedOps(): Promise<OfflineOp[]> {
  if (!hasIndexedDb()) return [];
  const db = await openDb();
  const ops = await new Promise<OfflineOp[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result as OfflineOp[]) || []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return ops.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function removeOp(id: number): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function countQueued(): Promise<number> {
  if (!hasIndexedDb()) return 0;
  const db = await openDb();
  const n = await new Promise<number>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return n;
}

// ── Validation stricte ────────────────────────────────────────

function isPositiveInt(v: unknown): boolean {
  return typeof v === 'number' && Number.isInteger(v) && v > 0;
}

function isValidCreditTx(payload: unknown): payload is CreditTxPayload {
  const p = payload as CreditTxPayload;
  if (!p || typeof p !== 'object') return false;
  if (!['credit', 'payment', 'deposit', 'adjustment'].includes(p.type)) return false;
  if (!isPositiveInt(p.customerId)) return false;
  if (typeof p.amount !== 'number' || !Number.isFinite(p.amount) || p.amount === 0) return false;
  return true;
}

function isValidSale(payload: unknown): payload is SalePayload {
  const p = payload as SalePayload;
  if (!p || typeof p !== 'object') return false;
  if (!Array.isArray(p.items) || p.items.length === 0 || p.items.length > 60) return false;
  if (!['cash', 'card'].includes(p.paymentMethod)) return false;
  if (typeof p.total !== 'number' || !Number.isFinite(p.total) || p.total < 0) return false;
  return p.items.every(
    (it) =>
      it &&
      isPositiveInt(it.productId) &&
      typeof it.quantity === 'number' &&
      Number.isInteger(it.quantity) &&
      it.quantity > 0 &&
      typeof it.unitPrice === 'number' &&
      Number.isFinite(it.unitPrice) &&
      it.unitPrice >= 0,
  );
}

function isValidStockMovement(payload: unknown): payload is StockMovementPayload {
  const p = payload as StockMovementPayload;
  if (!p || typeof p !== 'object') return false;
  if (!isPositiveInt(p.productId)) return false;
  if (typeof p.delta !== 'number' || !Number.isInteger(p.delta) || p.delta === 0) return false;
  if (typeof p.userId !== 'number' || !Number.isFinite(p.userId)) return false;
  return true;
}

function isValidProductUpdate(payload: unknown): payload is ProductUpdatePayload {
  const p = payload as ProductUpdatePayload;
  if (!p || typeof p !== 'object') return false;
  if (!isPositiveInt(p.productId)) return false;
  if (!p.patch || typeof p.patch !== 'object') return false;
  return true;
}

// ── Écritures réelles (au retour du réseau) ───────────────────

async function applyCreditTx(merchantId: number, payload: CreditTxPayload): Promise<void> {
  const { error } = await supabase.from('credit_transactions').insert({
    merchant_id: merchantId,
    customer_id: payload.customerId,
    type: payload.type,
    amount: payload.amount,
    description: payload.description || null,
    payment_method: payload.paymentMethod || null,
    created_by: merchantId,
    reverses_transaction_id: payload.reversesTransactionId ?? null,
  });
  if (error) throw error;
}

async function applySale(merchantId: number, payload: SalePayload): Promise<void> {
  const now = new Date().toISOString();
  const { data: orderData, error: orderError } = await supabase
    .from('order_headers')
    .insert({
      customer_id: String(merchantId),
      status: 'paid',
      currency: payload.currency,
      subtotal_items: payload.subtotal,
      tax_total: payload.tax,
      payment_provider: payload.paymentMethod === 'cash' ? 'cash' : 'card',
      created_at: now,
      updated_at: now,
    })
    .select('id')
    .single();
  if (orderError) throw orderError;

  const orderId = orderData?.id;
  const { error: itemsError } = await supabase.from('order_items').insert(
    payload.items.map((it) => ({
      order_id: orderId,
      product_id: String(it.productId),
      product_name: it.name,
      quantity: it.quantity,
      unit_price: it.unitPrice,
      final_price: it.unitPrice,
      subtotal: it.unitPrice * it.quantity,
      created_at: now,
    })),
  );
  if (itemsError) throw itemsError;

  // Décrémente le stock pour chaque produit vendu hors-ligne
  for (const it of payload.items) {
    const { data: prod } = await supabase
      .from('product_items')
      .select('stock')
      .eq('id', it.productId)
      .maybeSingle();
    const current = Number(prod?.stock ?? 0);
    await supabase
      .from('product_items')
      .update({ stock: Math.max(0, current - it.quantity), updated_at: now })
      .eq('id', it.productId);
  }
}

async function applyStockMovement(
  merchantId: number,
  payload: StockMovementPayload,
): Promise<void> {
  const { data: prod } = await supabase
    .from('product_items')
    .select('stock')
    .eq('id', payload.productId)
    .maybeSingle();
  const current = Number(prod?.stock ?? 0);
  const nouveau = Math.max(0, current + payload.delta);
  const now = new Date().toISOString();

  const { error: upErr } = await supabase
    .from('product_items')
    .update({ stock: nouveau, updated_at: now })
    .eq('id', payload.productId);
  if (upErr) throw upErr;

  const { error: moveErr } = await supabase.from('mouvementstock').insert({
    idcommerce: merchantId,
    idarticle: payload.productId,
    quantiteactuelle: current,
    quantiteajouter: payload.delta,
    stocktotal: nouveau,
    date: now,
    iduser: payload.userId,
    username: payload.username || `Utilisateur #${payload.userId}`,
    motif: payload.motif || 'Mouvement hors ligne',
  });
  if (moveErr) throw moveErr;
}

async function applyProductUpdate(
  merchantId: number,
  payload: ProductUpdatePayload,
): Promise<void> {
  const { error } = await supabase
    .from('product_items')
    .update({ ...payload.patch, updated_at: new Date().toISOString() })
    .eq('id', payload.productId)
    .eq('idcommerce', merchantId);
  if (error) throw error;
}

/**
 * Rejoue la file d'attente. `merchantId` provient TOUJOURS de la session.
 * Renvoie le nombre d'opérations synchronisées et échouées.
 */
export async function syncOfflineQueue(
  merchantId: number,
): Promise<{ synced: number; failed: number; remaining: number }> {
  const ops = await getQueuedOps();
  let synced = 0;
  let failed = 0;
  for (const op of ops) {
    if (op.id == null) continue;
    try {
      if (op.kind === 'credit_tx' && isValidCreditTx(op.payload)) {
        await applyCreditTx(merchantId, op.payload);
        await removeOp(op.id);
        synced += 1;
      } else if (op.kind === 'sale' && isValidSale(op.payload)) {
        await applySale(merchantId, op.payload);
        await removeOp(op.id);
        synced += 1;
      } else if (op.kind === 'stock_movement' && isValidStockMovement(op.payload)) {
        await applyStockMovement(merchantId, op.payload);
        await removeOp(op.id);
        synced += 1;
      } else if (op.kind === 'product_update' && isValidProductUpdate(op.payload)) {
        await applyProductUpdate(merchantId, op.payload);
        await removeOp(op.id);
        synced += 1;
      } else {
        // Entrée invalide / altérée : on la retire pour ne pas bloquer la file.
        await removeOp(op.id);
        failed += 1;
      }
    } catch {
      failed += 1; // on garde l'opération pour un nouvel essai
    }
  }
  window.dispatchEvent(new CustomEvent('zifek-queue-changed'));
  const remaining = await countQueued();
  return { synced, failed, remaining };
}